// ========================================
// COMPONENTE DE IMPRESSÃO DE PROPOSTA
// src/components/ImpressaoProposta.jsx
// ========================================
// Modelo padronizado A4 para impressão de propostas
// Usado em: NovoPedido, ConsultaPedidos

import React, { forwardRef } from 'react';

// Função para formatar moeda
const formatarMoeda = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '0,00';
  return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Função para limpar nome de produto removendo código no início (ex: "R Rodaforro..." vira "Rodaforro...")
const limparNomeProduto = (nome) => {
  if (!nome) return nome;
  // Remove código de 1-3 caracteres seguido de espaço no início do nome
  return nome.replace(/^[A-Z0-9]{1,3}\s+/, '');
};

// Função para formatar data (trata YYYY-MM-DD sem criar Date para evitar problema de timezone)
const formatarData = (data) => {
  if (!data) return '';
  try {
    // Se já está no formato dd/mm/yyyy, retorna direto
    if (typeof data === 'string' && data.includes('/')) return data;
    // Se está no formato YYYY-MM-DD, converte manualmente sem criar Date (evita UTC shift)
    if (typeof data === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data)) {
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    }
    // Para timestamps completos, usa timezone de São Paulo
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return data;
  }
};

// Componente de Impressão (usando forwardRef para permitir ref externa)
const ImpressaoProposta = forwardRef(({ pedido, cliente, forros, acessorios, produtosEspeciais = [], formaPagamento, observacao, tabela = 1, valorFrete = 0, cargoUsuario = '' }, ref) => {
  
  // Verificar se pode ver frete (Vendas, Diretoria, Admin, Administrativo)
  const cargoLower = (cargoUsuario || '').toLowerCase();
  const podeVerFrete = cargoLower === 'vendas' || cargoLower === 'diretoria' || cargoLower === 'admin' || cargoLower === 'administrativo';

  // Verificar se é ORÇAMENTO para exibir a tarja
  // A lógica é simples: se tipo_documento NÃO for "PEDIDO", então é orçamento
  const tipoDocumento = (pedido?.tipo_documento || '').toUpperCase();
  const isOrcamento = tipoDocumento !== 'PEDIDO';
  
  // Filtrar itens por tabela - melhorado para pegar ordem também
  const forrosFiltrados = forros?.filter(f => {
    const tipoTabela = f.tipo_tabela || f.tabela || f.ordem || 'Tabela 1';
    const tabelaStr = String(tipoTabela).toLowerCase();
    return tabelaStr.includes(`${tabela}`) || tabelaStr === `tabela ${tabela}`;
  }) || [];
  
  const acessoriosFiltrados = acessorios?.filter(a => {
    const tipoTabela = a.tipo_tabela || a.tabela || a.ordem || 'Tabela 1';
    const tabelaStr = String(tipoTabela).toLowerCase();
    return tabelaStr.includes(`${tabela}`) || tabelaStr === `tabela ${tabela}`;
  }) || [];

  // Calcular totais
  const totalForro = forrosFiltrados.reduce((acc, f) => acc + (f.total || f.valor_total || 0), 0);
  const totalM2 = forrosFiltrados.reduce((acc, f) => acc + (f.m2 || f.qtdade || 0), 0);
  const totalAcessorios = acessoriosFiltrados.reduce((acc, a) => acc + (a.total || a.valor_total || 0), 0);
  
  // Calcular total de produtos especiais
  const totalProdutosEspeciais = (produtosEspeciais || []).reduce((acc, p) => acc + (parseFloat(p.total) || 0), 0);
  
  // Total da proposta inclui produtos especiais
  const totalProposta = totalForro + totalAcessorios + totalProdutosEspeciais;

  // Processar formas de pagamento (pode ser array de objetos com forma e observacao)
  const processarFormasPagamento = () => {
    let formas = formaPagamento || pedido?.formas_pagamento || pedido?.forma_pagamento || [];
    
    // Se for string, converter para array
    if (typeof formas === 'string') {
      try {
        formas = JSON.parse(formas);
      } catch {
        formas = formas.split(',').map(f => ({ forma: f.trim(), observacao: '' }));
      }
    }
    
    // Se for array de strings, converter para objetos
    if (Array.isArray(formas) && formas.length > 0 && typeof formas[0] === 'string') {
      formas = formas.map(f => ({ forma: f, observacao: '' }));
    }
    
    // Filtrar formas vazias
    return formas.filter(f => f && (f.forma || f.label || f));
  };

  const formasPagamentoProcessadas = processarFormasPagamento();

  return (
    <div ref={ref} className="print-container bg-white" style={{ 
      width: '210mm', 
      minHeight: '297mm', 
      padding: '8mm 12mm',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      color: '#000',
      boxSizing: 'border-box',
      pageBreakInside: 'avoid',
      position: 'relative'
    }}>
      
      {/* ========== TARJA DE ORÇAMENTO ========== */}
      {isOrcamento && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-35deg)',
          fontSize: '32px',
          fontWeight: 'bold',
          color: 'rgba(255, 0, 0, 0.25)',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 1000,
          textTransform: 'uppercase',
          letterSpacing: '3px',
          border: '4px solid rgba(255, 0, 0, 0.25)',
          padding: '15px 40px',
          borderRadius: '10px',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact'
        }}>
          <div>ORÇAMENTO</div>
          <div style={{ fontSize: '17px', marginTop: '5px' }}>VÁLIDO SOMENTE POR 7 DIAS</div>
        </div>
      )}
      
      {/* ========== CABEÇALHO ========== */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', borderBottom: '2px solid #0d5dfc', paddingBottom: '8px' }}>
        {/* Logo */}
        <div style={{ flex: '0 0 auto' }}>
          <img 
            src="/logo-primalia.svg" 
            alt="Primalia PVC" 
            style={{ height: '65px', width: 'auto' }}
          />
        </div>
        
        {/* Informações da Proposta */}
        <div style={{ textAlign: 'right' }}>
          <table style={{ borderCollapse: 'collapse', marginLeft: 'auto' }}>
            <tbody>
              <tr>
                <td style={{ padding: '2px 8px', textAlign: 'right', fontWeight: 'bold', fontSize: '12px' }}>Data:</td>
                <td style={{ padding: '2px 8px', border: '1px solid #ccc', minWidth: '80px', fontSize: '12px' }}>
                  {formatarData(pedido?.data || pedido?.created_at)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '2px 8px', textAlign: 'right', fontWeight: 'bold', fontSize: '12px' }}>
                  {pedido?.tipo_documento === 'ORCAMENTO' || pedido?.fase_atual === 'AGUARDANDO_PROXIMA_ETAPA' || 
                   pedido?.fase_atual === 'ORCAMENTO_APROVADO' || pedido?.fase_atual === 'SOLICITAR_EDICAO' ||
                   pedido?.fase_atual === 'APROVADO_COM_AJUSTES' || pedido?.fase_atual === 'ORCAMENTO_RECUSADO'
                    ? 'Orçamento Nº:' : 'Pedido Nº:'}
                </td>
                <td style={{ padding: '2px 8px', border: '1px solid #ccc', fontWeight: 'bold', color: '#0d5dfc', fontSize: '12px' }}>
                  {pedido?.numero_proposta || pedido?.proposta || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ========== DADOS DO CLIENTE ========== */}
      <div style={{ marginBottom: '10px', border: '1px solid #ccc' }}>
        <div style={{ backgroundColor: '#0d5dfc', color: 'white', padding: '4px 8px', fontWeight: 'bold', fontSize: '12px' }}>
          DADOS DO CLIENTE
        </div>
        <div style={{ padding: '6px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '2px', fontWeight: 'bold', width: '55px' }}>Cliente:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }} colSpan={3}>
                  {cliente?.nome || pedido?.cliente_nome || '-'}
                </td>
                <td style={{ padding: '2px', fontWeight: 'bold', width: '45px' }}>Bairro:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }}>
                  {cliente?.bairro || pedido?.bairro || '-'}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '2px', fontWeight: 'bold' }}>Endereço:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }} colSpan={3}>
                  {cliente?.endereco || pedido?.endereco || '-'}
                </td>
                <td style={{ padding: '2px', fontWeight: 'bold' }}>Estado:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }}>
                  {cliente?.estado || pedido?.estado || '-'}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '2px', fontWeight: 'bold' }}>Cidade:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }} colSpan={3}>
                  {cliente?.cidade || pedido?.cidade || '-'}
                </td>
                <td style={{ padding: '2px', fontWeight: 'bold' }}>CNPJ/CPF:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }}>
                  {cliente?.cnpj || pedido?.cnpj || '-'}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '2px', fontWeight: 'bold' }}>Contato:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }}>
                  {cliente?.telefone || cliente?.contato || pedido?.telefone || '-'}
                </td>
                <td style={{ padding: '2px', fontWeight: 'bold', width: '45px' }}>E-mail:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }} colSpan={3}>
                  {cliente?.email || pedido?.email || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ========== DADOS DA ENTREGA ========== */}
      <div style={{ marginBottom: '10px', border: '1px solid #ccc' }}>
        <div style={{ backgroundColor: '#00BEFF', color: 'white', padding: '4px 8px', fontWeight: 'bold', fontSize: '12px' }}>
          DADOS DA ENTREGA
        </div>
        <div style={{ padding: '6px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '2px', fontWeight: 'bold', width: '55px' }}>Endereço:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }} colSpan={3}>
                  {pedido?.endereco_entrega || cliente?.endereco || pedido?.endereco || '-'}
                </td>
                <td style={{ padding: '2px', fontWeight: 'bold', width: '45px' }}>Bairro:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }}>
                  {pedido?.bairro_entrega || cliente?.bairro || pedido?.bairro || '-'}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '2px', fontWeight: 'bold' }}>Cidade:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }} colSpan={3}>
                  {pedido?.cidade_entrega || cliente?.cidade || pedido?.cidade || '-'}
                </td>
                <td style={{ padding: '2px', fontWeight: 'bold' }}>Estado:</td>
                <td style={{ padding: '2px', borderBottom: '1px solid #eee' }}>
                  {pedido?.estado_entrega || cliente?.estado || pedido?.estado || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ========== TABELA DE FORRO ========== */}
      <div style={{ marginBottom: '8px', pageBreakInside: 'avoid' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr style={{ backgroundColor: '#0d5dfc', color: 'white' }}>
              <th style={{ padding: '4px', border: '1px solid #0d5dfc', textAlign: 'center', width: '55px' }}>QTDE</th>
              <th style={{ padding: '4px', border: '1px solid #0d5dfc', textAlign: 'center', width: '35px' }}>CÓD</th>
              <th style={{ padding: '4px', border: '1px solid #0d5dfc', textAlign: 'left' }}>DESCRIÇÃO FORRO</th>
              <th style={{ padding: '4px', border: '1px solid #0d5dfc', textAlign: 'center', width: '35px' }}>TIPO</th>
              <th style={{ padding: '4px', border: '1px solid #0d5dfc', textAlign: 'center', width: '45px' }}>QT. PEÇAS</th>
              <th style={{ padding: '4px', border: '1px solid #0d5dfc', textAlign: 'center', width: '45px' }}>COMPR.</th>
              <th style={{ padding: '4px', border: '1px solid #0d5dfc', textAlign: 'right', width: '70px' }}>R$ UNIT.</th>
              <th style={{ padding: '4px', border: '1px solid #0d5dfc', textAlign: 'right', width: '80px' }}>R$ TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {forrosFiltrados.length > 0 ? forrosFiltrados.map((forro, idx) => (
              <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {(forro.m2 || forro.qtdade || 0).toFixed(2)} m²
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {forro.codigo || forro.cod || forro.codigo_produto || '-'}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd' }}>
                  {limparNomeProduto(forro.produto || forro.descricao || forro.nome_produto || '-')}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {forro.tip || forro.tipo || 'BCO'}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {forro.qtde || forro.quantidade || '-'}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {forro.comp || forro.comprimento || '-'}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  R$ {formatarMoeda(forro.preco || forro.valor_unitario)}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  R$ {formatarMoeda(forro.total || forro.valor_total)}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>
                  Nenhum forro nesta tabela
                </td>
              </tr>
            )}
            {/* Linha de Total */}
            <tr style={{ backgroundColor: '#e8f4fc' }}>
              <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold' }}>
                {totalM2.toFixed(2)} m²
              </td>
              <td colSpan={6} style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>
                VALOR TOTAL FORRO
              </td>
              <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', color: '#0d5dfc', whiteSpace: 'nowrap' }}>
                R$ {formatarMoeda(totalForro)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ========== TABELA DE ACESSÓRIOS ========== */}
      <div style={{ marginBottom: '8px', pageBreakInside: 'avoid' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr style={{ backgroundColor: '#00BEFF', color: 'white' }}>
              <th style={{ padding: '4px', border: '1px solid #00BEFF', textAlign: 'center', width: '55px' }}>QTDE</th>
              <th style={{ padding: '4px', border: '1px solid #00BEFF', textAlign: 'center', width: '35px' }}>CÓD.</th>
              <th style={{ padding: '4px', border: '1px solid #00BEFF', textAlign: 'left' }}>DESCRIÇÃO ACESSÓRIOS</th>
              <th style={{ padding: '4px', border: '1px solid #00BEFF', textAlign: 'center', width: '35px' }}>TIPO</th>
              <th style={{ padding: '4px', border: '1px solid #00BEFF', textAlign: 'center', width: '55px' }}>QT. PEÇAS</th>
              <th style={{ padding: '4px', border: '1px solid #00BEFF', textAlign: 'right', width: '70px' }}>R$ UNIT.</th>
              <th style={{ padding: '4px', border: '1px solid #00BEFF', textAlign: 'right', width: '80px' }}>R$ TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {acessoriosFiltrados.length > 0 ? acessoriosFiltrados.map((acessorio, idx) => (
              <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {acessorio.qtde || acessorio.quantidade || '-'} {acessorio.unidade || 'und'}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {acessorio.codigo || acessorio.cod || acessorio.codigo_produto || '-'}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd' }}>
                  {limparNomeProduto(acessorio.produto || acessorio.descricao || acessorio.nome_produto || '-')}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {acessorio.tip || acessorio.tipo || 'BCO'}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                  {acessorio.qt_pecas || acessorio.qtde || '-'}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  R$ {formatarMoeda(acessorio.preco || acessorio.valor_unitario)}
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  R$ {formatarMoeda(acessorio.total || acessorio.valor_total)}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={7} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>
                  Nenhum acessório nesta tabela
                </td>
              </tr>
            )}
            {/* Linha de Total Acessórios */}
            <tr style={{ backgroundColor: '#e0f7fa' }}>
              <td colSpan={6} style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>
                VALOR TOTAL ACESSÓRIOS
              </td>
              <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', color: '#00BEFF', whiteSpace: 'nowrap' }}>
                R$ {formatarMoeda(totalAcessorios)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ========== TABELA DE PRODUTOS ESPECIAIS (só aparece se houver) ========== */}
      {produtosEspeciais && produtosEspeciais.length > 0 && (
        <div style={{ marginBottom: '8px', pageBreakInside: 'avoid' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ backgroundColor: '#6b7280', color: 'white' }}>
                <th style={{ padding: '4px', border: '1px solid #6b7280', textAlign: 'center', width: '55px' }}>QTDE</th>
                <th style={{ padding: '4px', border: '1px solid #6b7280', textAlign: 'center', width: '35px' }}>CÓD.</th>
                <th style={{ padding: '4px', border: '1px solid #6b7280', textAlign: 'left' }}>DESCRIÇÃO DEMAIS PRODUTOS</th>
                <th style={{ padding: '4px', border: '1px solid #6b7280', textAlign: 'right', width: '70px' }}>R$ UNIT.</th>
                <th style={{ padding: '4px', border: '1px solid #6b7280', textAlign: 'right', width: '80px' }}>R$ TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {produtosEspeciais.map((produto, idx) => (
                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {produto.quantidade || produto.qtde || 1}
                  </td>
                  <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {produto.produto_codigo || produto.codigo || produto.cod || '-'}
                  </td>
                  <td style={{ padding: '3px', border: '1px solid #ddd' }}>
                    {limparNomeProduto(produto.produto_nome || produto.produto || produto.nome_produto || '-')}
                  </td>
                  <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    R$ {formatarMoeda(produto.preco_unitario || produto.preco)}
                  </td>
                  <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    R$ {formatarMoeda(produto.total)}
                  </td>
                </tr>
              ))}
              {/* Linha de Total Produtos Especiais */}
              <tr style={{ backgroundColor: '#f3f4f6' }}>
                <td colSpan={4} style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>
                  VALOR TOTAL DEMAIS PRODUTOS
                </td>
                <td style={{ padding: '3px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', color: '#374151', whiteSpace: 'nowrap' }}>
                  R$ {formatarMoeda(totalProdutosEspeciais)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ========== VALOR TOTAL PEDIDO (separado) ========== */}
      <div style={{ marginBottom: '10px', pageBreakInside: 'avoid' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <tbody>
            <tr style={{ backgroundColor: '#1a1a2e', color: 'white' }}>
              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>
                VALOR TOTAL PEDIDO
              </td>
              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: '15px', width: '120px', whiteSpace: 'nowrap' }}>
                R$ {formatarMoeda(totalProposta)}
              </td>
            </tr>
            {podeVerFrete && valorFrete > 0 && (
              <>
                <tr style={{ backgroundColor: '#e5e7eb', color: '#374151' }}>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'bold', fontSize: '13px' }}>
                    VALOR DO FRETE
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'bold', fontSize: '14px', width: '100px' }}>
                    R$ {formatarMoeda(valorFrete)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* ========== FORMA DE PAGAMENTO E OBSERVAÇÕES ========== */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', pageBreakInside: 'avoid' }}>
        {/* Forma de Pagamento */}
        <div style={{ flex: '1', border: '1px solid #ccc' }}>
          <div style={{ backgroundColor: '#0d5dfc', color: 'white', padding: '4px 8px', fontWeight: 'bold', fontSize: '12px' }}>
            FORMA DE PAGAMENTO
          </div>
          <div style={{ padding: '8px', fontSize: '11px' }}>
            {formasPagamentoProcessadas.length > 0 ? (
              <div>
                {formasPagamentoProcessadas.map((fp, idx) => {
                  const formaLabel = fp.forma || fp.label || fp;
                  const obs = fp.observacao || '';
                  return (
                    <div key={idx} style={{ 
                      padding: '4px 0', 
                      borderBottom: idx < formasPagamentoProcessadas.length - 1 ? '1px solid #eee' : 'none' 
                    }}>
                      <div style={{ fontWeight: 'bold' }}>• {formaLabel}</div>
                      {obs && <div style={{ color: '#666', fontSize: '10px', marginLeft: '10px', fontStyle: 'italic' }}>Obs: {obs}</div>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: '#999', fontStyle: 'italic' }}>Não informado</div>
            )}
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #ddd' }}>
              <table style={{ width: '100%', fontSize: '11px' }}>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>VALOR TOTAL:</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#0d5dfc' }}>
                      R$ {formatarMoeda(podeVerFrete && valorFrete > 0 ? totalProposta + valorFrete : totalProposta)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Observações */}
        <div style={{ flex: '1', border: '1px solid #ccc' }}>
          <div style={{ backgroundColor: '#666', color: 'white', padding: '4px 8px', fontWeight: 'bold', fontSize: '12px' }}>
            OBSERVAÇÕES
          </div>
          <div style={{ padding: '8px', minHeight: '50px', fontSize: '11px' }}>
            {observacao || pedido?.observacao || pedido?.observacoes || (
              <span style={{ color: '#999', fontStyle: 'italic' }}>Nenhuma observação registrada</span>
            )}
          </div>
        </div>
      </div>

      {/* ========== ASSINATURAS ========== */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '10px', marginTop: '30px', pageBreakInside: 'avoid' }}>
        <div style={{ flex: '1', textAlign: 'center' }}>
          <div style={{ borderBottom: '1px solid #000', marginBottom: '4px', paddingBottom: '20px' }}></div>
          <div style={{ fontWeight: 'bold', fontSize: '11px' }}>{pedido?.vendedor || 'REPRESENTANTE'}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>REPRESENTANTE</div>
        </div>
        <div style={{ flex: '1', textAlign: 'center' }}>
          <div style={{ borderBottom: '1px solid #000', marginBottom: '4px', paddingBottom: '20px' }}></div>
          <div style={{ fontWeight: 'bold', fontSize: '11px' }}>COMPRADOR</div>
          <div style={{ fontSize: '10px', color: '#666' }}>Data de Aprovação: ____/____/________</div>
        </div>
      </div>

      {/* ========== RODAPÉ ========== */}
      <div style={{ 
        borderTop: '2px solid #0d5dfc', 
        paddingTop: '8px', 
        marginTop: 'auto',
        fontSize: '10px',
        textAlign: 'center',
        color: '#666',
        pageBreakInside: 'avoid'
      }}>
        <div style={{ fontWeight: 'bold', color: '#0d5dfc', marginBottom: '2px', fontSize: '11px' }}>
          PRIMALIA PVC - Forros e Acabamentos
        </div>
        <div>
          Rua Getúlio Dal Pasquale, 200 - São Francisco - CEP: 85504-610 - Pato Branco - Paraná
        </div>
        <div>
          Fone: (46) 3040-0290 | E-mail: comercial@primaliapvc.com.br
        </div>
      </div>
    </div>
  );
});

ImpressaoProposta.displayName = 'ImpressaoProposta';

export default ImpressaoProposta;
